/**
 * Health screen. Five sub tabs inside #healthRoot: today, body, heart,
 * training load, and strength and endurance. Everything talks to /api/health,
 * which writes the same doc.body entries the journal body log already uses, so
 * one day is one entry however it was logged.
 *
 * Charts are drawn here as inline SVG, no library, brand tokens only, and a
 * line is never drawn through fewer than three points. Days with nothing
 * logged break the line instead of being guessed at.
 *
 * BMI is only ever a number the player logged. The Worker leaves it out
 * entirely for a young account, and this screen never labels it, ranks it or
 * colours it. No calorie targets, no goal weights, nothing medical.
 */

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const TABS = [['today', 'Today'], ['body', 'Body'], ['heart', 'Heart'], ['load', 'Training load'], ['gym', 'Strength and endurance']];
const SCALES = ['fatigue', 'soreness', 'energy'];
const WINDOW_DAYS = 90;     // what the screen asks the Worker for
const LOAD_DAYS = 30;       // what the training load chart draws
const NOT_MEDICAL = 'This is training information, not medical advice. Anything about your health that worries you goes to a parent, a coach or a doctor.';

const COLOR = {
  weight: 'var(--hot)', bmi: 'var(--cool)',
  resting: 'var(--cool)', training: 'var(--hot)', peak: 'var(--grape)',
  fatigue: 'var(--hot)', soreness: 'var(--grape)', energy: 'var(--good)',
  session: 'var(--cool)',
};

const n0 = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };
const round1 = (n) => Math.round(n * 10) / 10;
const fmt = (v) => Math.round(n0(v)).toLocaleString();
const fmt1 = (v) => (Math.abs(v - Math.round(v)) < 0.05 ? String(Math.round(v)) : String(round1(v)));
const d2 = (n) => String(n).padStart(2, '0');
const localDay = (d) => `${d.getFullYear()}-${d2(d.getMonth() + 1)}-${d2(d.getDate())}`;
const dayOfIso = (iso) => { const t = Date.parse(iso); return Number.isFinite(t) ? localDay(new Date(t)) : ''; };
const cap = (s) => String(s || '').charAt(0).toUpperCase() + String(s || '').slice(1);

function dayLabel(key) {
  if (!key) return '';
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  if (key === localDay(today)) return 'Today';
  if (key === localDay(yest)) return 'Yesterday';
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}
const shortDay = (key) => {
  if (!key) return '';
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
/** The last n local days, oldest first. */
function dayGrid(n) {
  const out = [];
  for (let i = n - 1; i >= 0; i--) out.push(localDay(new Date(Date.now() - i * 864e5)));
  return out;
}

/* ------------------------------------------------------------------ state
 * Module level, so a half filled form and the sub tab survive a repaint and a
 * trip to another view. */
let ctx = null, root = null, panel = null, tabsEl = null;
const S = {
  tab: 'today',
  data: null, err: null,
  sessions: null, sessionsErr: null,
  today: {},                                       // unsaved 1 to 5 picks and mood on the Today form
  nums: {},                                        // unsaved numbers on the Today form
  lift: { lift: '', kg: '', reps: '' },
  cardio: { kind: 'run', minutes: '', distanceKm: '' },
};

/* ------------------------------------------------------------------- boot */

export function mount(c) {
  ctx = c;
  root = document.getElementById('healthRoot');
  if (!root) return;
  root.innerHTML = `
    <div class="ntabs htabs">${TABS.map(([id, label]) => `<button type="button" data-tab="${id}">${esc(label)}</button>`).join('')}</div>
    <div class="grid-stats hstrip" id="hStrip"></div>
    <div id="hPanel"></div>
    <p class="hplain" id="hPlain">${esc(NOT_MEDICAL)}</p>
    <div class="modal" id="hModal"><div class="sheet" id="hSheet"></div></div>`;
  panel = root.querySelector('#hPanel');
  tabsEl = root.querySelector('.htabs');
  tabsEl.addEventListener('click', (e) => {
    const b = e.target.closest('button[data-tab]');
    if (!b) return;
    S.tab = b.dataset.tab;
    paintTabs();
    paint();
  });
  root.querySelector('#hModal').addEventListener('click', (e) => { if (e.target.id === 'hModal') closeSheet(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeSheet(); });
  paintTabs();
}

export async function render() {
  if (!root) return;
  paintTabs();
  if (!S.data) paint();        // never leave the panel blank while the first call is out
  await refresh();
}

/** Every call carries the browser's offset so a day rolls over where the player is. */
function hurl(path, params) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) if (v) q.set(k, v);
  q.set('tz', String(new Date().getTimezoneOffset()));
  return `/api/health${path}?${q}`;
}

async function refresh() {
  try {
    S.data = await ctx.api(hurl('', { days: WINDOW_DAYS }));
    S.err = null;
  } catch (e) { S.err = e.message; }
  paint();
}

/** The sessions list, for the training load tab. Its failure is not fatal. */
async function sessions() {
  if (Array.isArray(S.sessions)) return S.sessions;
  try { S.sessions = await ctx.api('/api/sessions'); S.sessionsErr = null; }
  catch (e) { S.sessions = []; S.sessionsErr = e.message; }
  return S.sessions;
}

/* ------------------------------------------------------------------ paint */

function paintTabs() {
  if (!tabsEl) return;
  tabsEl.querySelectorAll('button[data-tab]').forEach((b) => b.classList.toggle('on', b.dataset.tab === S.tab));
}

function paint() {
  if (!panel) return;
  paintStrip();
  const plain = root?.querySelector('#hPlain');
  if (plain) plain.textContent = S.data?.prompts?.notMedical || NOT_MEDICAL;
  if (S.err) {
    panel.innerHTML = `<div class="card"><h3>Health</h3><p class="sub">${esc(S.err)}</p>
      <button class="btn" id="hRetry" style="margin-top:12px">Try again</button></div>`;
    bind('#hRetry', 'click', refresh);
    return;
  }
  if (!S.data) { panel.innerHTML = '<div class="card"><h3>Health</h3><p class="sub">Loading your log.</p></div>'; return; }
  const fn = { today: paintToday, body: paintBody, heart: paintHeart, load: paintLoad, gym: paintGym }[S.tab] || paintToday;
  try {
    const out = fn();
    if (out && typeof out.catch === 'function') out.catch(failed);
  } catch (e) { failed(e); }
}

function failed(e) {
  if (panel) panel.innerHTML = `<div class="card"><h3>Health</h3><p class="sub">${esc(e?.message || 'That did not load.')}</p></div>`;
}

const bind = (sel, ev, fn, scope = panel) => scope?.querySelectorAll(sel).forEach((el) => el.addEventListener(ev, fn));
const entries = () => (Array.isArray(S.data?.entries) ? S.data.entries.filter(Boolean) : []);
const ranges = () => S.data?.ranges || {};
const showBmi = () => Boolean(S.data?.showBmi);
const trendOf = (key) => (S.data?.trends || []).find((t) => t && t.key === key) || null;
/** The entry for a local day, or null. */
const entryOn = (key) => entries().find((e) => dayOfIso(e.at) === key) || null;
const todayEntry = () => entryOn(localDay(new Date()));
const countOf = (key) => entries().filter((e) => Number.isFinite(Number(e[key]))).length;

function paintStrip() {
  const el = root?.querySelector('#hStrip');
  if (!el) return;
  const latest = S.data?.latest;
  if (!latest) { el.innerHTML = ''; return; }
  const t = todayEntry() || {};
  const tiles = [
    latest.weightKg && `<div class="stat"><b>${fmt1(latest.weightKg)}</b><small>kg last logged</small></div>`,
    showBmi() && latest.bmi && `<div class="stat"><b>${fmt1(latest.bmi)}</b><small>BMI you logged</small></div>`,
    latest.restingHr && `<div class="stat"><b>${fmt(latest.restingHr)}</b><small>bpm resting</small></div>`,
    latest.sleepHours != null && `<div class="stat"><b>${fmt1(latest.sleepHours)}</b><small>hours sleep</small></div>`,
    t.fatigue && `<div class="stat"><b>${fmt(t.fatigue)}</b><small>fatigue today</small></div>`,
    `<div class="stat"><b>${fmt(entries().length)}</b><small>days logged</small></div>`,
  ].filter(Boolean);
  el.innerHTML = tiles.join('');
}

/* =============================================================== 1. TODAY */

function paintToday() {
  const t = todayEntry();
  const prompts = S.data?.prompts || {};
  const scales = Array.isArray(prompts.scales) && prompts.scales.length ? prompts.scales : SCALES.map((key) => ({ key, label: cap(key), help: '', low: 'Low', high: 'High' }));
  const moods = Array.isArray(prompts.moods) ? prompts.moods : [];
  const pick = (key) => (S.today[key] != null ? S.today[key] : (t ? num(t[key]) : null));
  const mood = S.today.mood != null ? S.today.mood : (t?.mood || '');
  const field = (id, key) => {
    const r = ranges()[key] || {};
    const saved = t && t[key] != null ? String(t[key]) : '';
    const value = S.nums[key] !== undefined ? S.nums[key] : saved;
    return `<label class="hlab">${esc(r.label || key)} <em>${esc(r.unit || '')}</em>
      <input type="number" id="${id}" data-n="${esc(key)}" inputmode="decimal" min="${r.min ?? 0}" max="${r.max ?? 999}" step="${r.step ?? 1}" value="${esc(value)}"></label>`;
  };

  panel.innerHTML = `
    <div class="hgrid">
      <div class="card">
        <h3>How today feels</h3>
        ${scales.map((s) => `
          <div class="hscale">
            <div class="hscale-h"><b>${esc(s.label)}</b>${s.help ? `<small>${esc(s.help)}</small>` : ''}</div>
            <div class="hpick" data-key="${esc(s.key)}">${[1, 2, 3, 4, 5].map((v) =>
              `<button type="button" data-key="${esc(s.key)}" data-v="${v}" class="${pick(s.key) === v ? 'on' : ''}" aria-pressed="${pick(s.key) === v}">${v}</button>`).join('')}</div>
            <div class="hends"><span>${esc(s.low || '')}</span><span>${esc(s.high || '')}</span></div>
          </div>`).join('')}
        ${moods.length ? `
          <div class="hscale">
            <div class="hscale-h"><b>Mood</b><small>One word for the day.</small></div>
            <div class="hmoods">${moods.map((m) =>
              `<button type="button" class="chip hmood ${mood === m ? 'on' : ''}" data-mood="${esc(m)}">${esc(cap(m))}</button>`).join('')}</div>
          </div>` : ''}
      </div>

      <div class="card">
        <h3>The numbers</h3>
        <div class="hform">
          ${field('hWeight', 'weightKg')}
          ${field('hSleep', 'sleepHours')}
          ${field('hResting', 'restingHr')}
        </div>
        <div class="hactions">
          <button class="btn primary" id="hSave">Save today</button>
          <button class="btn" id="hClear">Start over</button>
        </div>
        <p class="sub" style="margin-top:12px">One entry a day. Saving folds what you just put in to today's log, so you can weigh in this morning and rate your legs tonight.</p>
        ${t ? `<div class="hsaved"><b>Saved today</b><p>${esc(lineOf(t))}</p></div>` : ''}
      </div>
    </div>`;

  bind('.hpick button', 'click', (e) => {
    const key = e.currentTarget.dataset.key;
    const v = Number(e.currentTarget.dataset.v);
    S.today[key] = v;
    e.currentTarget.parentElement.querySelectorAll('button').forEach((b) => {
      const on = Number(b.dataset.v) === S.today[key];
      b.classList.toggle('on', on);
      b.setAttribute('aria-pressed', String(on));
    });
  });
  bind('.hmood', 'click', (e) => {
    const m = e.currentTarget.dataset.mood;
    S.today.mood = S.today.mood === m ? '' : m;
    panel.querySelectorAll('.hmood').forEach((b) => b.classList.toggle('on', b.dataset.mood === S.today.mood));
  });
  bind('[data-n]', 'input', (e) => { S.nums[e.target.dataset.n] = e.target.value; });
  bind('#hSave', 'click', saveToday);
  bind('#hClear', 'click', () => { S.today = {}; S.nums = {}; paint(); });
}

/** A plain one line read of an entry, the same order the journal note uses. */
function lineOf(e) {
  if (!e) return '';
  const s = e.strength || [];
  const c = e.endurance;
  return [
    e.weightKg && `${fmt1(e.weightKg)} kg`,
    e.heightCm && `${fmt1(e.heightCm)} cm`,
    showBmi() && e.bmi && `BMI ${fmt1(e.bmi)}`,
    e.sleepHours != null && `${fmt1(e.sleepHours)} h sleep`,
    e.restingHr && `${fmt(e.restingHr)} bpm resting`,
    e.trainingHr && `${fmt(e.trainingHr)} bpm training`,
    e.peakHr && `${fmt(e.peakHr)} bpm peak`,
    e.fatigue && `fatigue ${e.fatigue} of 5`,
    e.soreness && `soreness ${e.soreness} of 5`,
    e.energy && `energy ${e.energy} of 5`,
    e.mood && `feeling ${e.mood}`,
    e.waterCups != null && `${fmt(e.waterCups)} cups water`,
    s.length && `${s.length} lift${s.length === 1 ? '' : 's'}`,
    c && `${c.kind}${c.minutes ? ` ${fmt(c.minutes)} min` : ''}${c.distanceKm ? ` ${fmt1(c.distanceKm)} km` : ''}`,
    e.note,
  ].filter(Boolean).join(' \u00b7 ');
}

async function saveToday() {
  const body = {};
  for (const key of SCALES) if (S.today[key] != null) body[key] = S.today[key];
  if (S.today.mood) body.mood = S.today.mood;
  const t = todayEntry();
  let typed = false;
  for (const [id, key] of [['hWeight', 'weightKg'], ['hSleep', 'sleepHours'], ['hResting', 'restingHr']]) {
    const raw = (panel.querySelector(`#${id}`)?.value ?? '').trim();
    // Only what they actually typed goes up. Clearing a box is done on the entry
    // itself in Body, so a blank box here never wipes this morning's log.
    if (raw === '') continue;
    typed = true;
    if (t && Number(t[key]) === Number(raw)) continue;
    body[key] = Number(raw);
  }
  if (!Object.keys(body).length) return ctx.toast(typed ? 'That is already what today says.' : 'Put something in first.', true);
  await post(body, 'Saved. That is today.');
}

/** POST a merge into today and repaint. Every tab's save comes through here. */
async function post(body, okMessage) {
  const btns = panel.querySelectorAll('button');
  btns.forEach((b) => { b.disabled = true; });
  try {
    await ctx.api(hurl(''), { method: 'POST', body: JSON.stringify(body) });
    S.today = {}; S.nums = {};
    ctx.toast(okMessage);
    await refresh();
    ctx.refresh?.journal?.();
  } catch (e) {
    ctx.toast(e.message, true);
    btns.forEach((b) => { b.disabled = false; });
  }
}

/* ================================================================ 2. BODY */

function paintBody() {
  const list = entries();
  const weight = trendOf('weightKg');
  const bmi = showBmi() ? trendOf('bmi') : null;
  const latestHeight = list.find((e) => Number(e.heightCm) > 0)?.heightCm || '';
  const hr = ranges().heightCm || {};

  panel.innerHTML = `
    <div class="card">
      <h3>Weight over time</h3>
      ${weight ? trendChart(weight) : notEnough(countOf('weightKg'), 'weight')}
      ${weight ? `<p class="sub">${esc(moveLine(weight))}</p>` : ''}
    </div>
    ${bmi ? `<div class="card">
      <h3>BMI over time</h3>
      ${trendChart(bmi)}
      <p class="sub">${esc(moveLine(bmi))} This is the number worked out from the height and weight you logged, nothing more.</p>
    </div>` : ''}
    <div class="hgrid">
      <div class="card">
        <h3>Your height</h3>
        <div class="hform">
          <label class="hlab">Height <em>${esc(hr.unit || 'cm')}</em>
            <input type="number" id="hHeight" inputmode="decimal" min="${hr.min ?? 80}" max="${hr.max ?? 250}" step="${hr.step ?? 0.5}" value="${esc(String(latestHeight || ''))}"></label>
        </div>
        <div class="hactions"><button class="btn primary" id="hHeightSave">Save height</button></div>
        <p class="sub" style="margin-top:12px">You only need this once. It is kept with today's entry and carried forward.</p>
      </div>
      <div class="card">
        <h3>What you logged</h3>
        ${list.length ? `<div class="hrows">${list.slice(0, 40).map((e) => row(e)).join('')}</div>`
          : '<p class="sub">Nothing logged yet. Put today in on the Today tab and this fills up.</p>'}
      </div>
    </div>`;

  bind('#hHeightSave', 'click', async () => {
    const raw = (panel.querySelector('#hHeight')?.value ?? '').trim();
    if (raw === '') return ctx.toast('Put your height in first.', true);
    await post({ heightCm: Number(raw) }, 'Height saved.');
  });
  bindRows();
}

const row = (e) => `<button type="button" class="hrow" data-id="${esc(e.id)}">
  <span class="hrow-d"><b>${esc(dayLabel(dayOfIso(e.at)))}</b><small>${esc(new Date(e.at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }))}</small></span>
  <span class="hrow-t">${esc(lineOf(e) || 'Nothing on this one')}</span>
</button>`;

function bindRows() {
  bind('.hrow', 'click', (e) => {
    const id = e.currentTarget.dataset.id;
    openSheet(entries().find((x) => String(x.id) === id));
  });
}

function notEnough(n, what) {
  return `<p class="sub">Not enough logged yet. You have ${fmt(n)} ${esc(what)} log${n === 1 ? '' : 's'} in the last ${WINDOW_DAYS} days, and a line needs three.</p>`;
}

/** Plain words for a trend. No verdict, no colour, just the move. */
function moveLine(t) {
  const unit = t.unit ? ` ${t.unit}` : '';
  const from = `${fmt1(t.first)}${unit} to ${fmt1(t.last)}${unit}`;
  if (t.direction === 'flat') return `${t.label}: ${from} across ${t.points.length} logs, no change.`;
  return `${t.label}: ${from} across ${t.points.length} logs, ${t.direction} ${fmt1(Math.abs(t.change))}${unit}.`;
}

/** One trend, drawn on a day grid so gaps in logging read as gaps. */
function trendChart(t) {
  const full = dayGrid(WINDOW_DAYS);
  const { grid, seriesList } = trimToData(full, [seriesOn(full, t.points)]);
  const values = seriesList[0];
  const { min, max } = bounds(values);
  return chart({
    grid, min, max, unit: t.unit || t.label.toLowerCase(),
    series: [{ label: t.label, color: COLOR[t.key === 'weightKg' ? 'weight' : t.key] || 'var(--hot)', values }],
    aria: `${t.label} on each day you logged it over the last ${WINDOW_DAYS} days`,
  });
}

/* =============================================================== 3. HEART */

function paintHeart() {
  const full = dayGrid(WINDOW_DAYS);
  const resting = trendOf('restingHr');
  const rest = trimToData(full, [seriesOn(full, resting ? resting.points : [])]);
  const train = trimToData(full, [seriesFrom(full, 'trainingHr'), seriesFrom(full, 'peakHr')]);
  const [training, peak] = train.seriesList;
  const hasTrain = training.some((v) => v != null) || peak.some((v) => v != null);
  const trainCount = countOf('trainingHr') + countOf('peakHr');
  const t = todayEntry();
  const field = (id, key) => {
    const r = ranges()[key] || {};
    return `<label class="hlab">${esc(r.label || key)} <em>${esc(r.unit || 'bpm')}</em>
      <input type="number" id="${id}" inputmode="numeric" min="${r.min ?? 40}" max="${r.max ?? 230}" step="1" value="${t && t[key] ? esc(String(t[key])) : ''}"></label>`;
  };
  const b = hasTrain ? bounds([...training, ...peak]) : { min: 0, max: 1 };

  panel.innerHTML = `
    <div class="card">
      <h3>Resting heart rate</h3>
      ${resting ? chart({
        grid: rest.grid, ...bounds(rest.seriesList[0]), unit: 'bpm',
        series: [{ label: 'Resting', color: COLOR.resting, values: rest.seriesList[0] }],
        aria: `Resting heart rate on each day you logged it over the last ${WINDOW_DAYS} days`,
      }) : notEnough(countOf('restingHr'), 'resting heart rate')}
      ${resting ? `<p class="sub">${esc(moveLine(resting))}</p>` : ''}
      <p class="hplain">${esc(S.data?.prompts?.restingHr || 'Resting heart rate is a training signal, not a diagnosis. Your own number over weeks says more than any single reading.')}</p>
    </div>
    <div class="card">
      <h3>While you train</h3>
      ${hasTrain ? `${chart({
        grid: train.grid, min: b.min, max: b.max, unit: 'bpm',
        series: [
          { label: 'Training average', color: COLOR.training, values: training },
          { label: 'Peak', color: COLOR.peak, values: peak },
        ],
        aria: `Training and peak heart rate on each day you logged them over the last ${WINDOW_DAYS} days`,
      })}${legend([{ label: 'Training average', color: COLOR.training }, { label: 'Peak', color: COLOR.peak }])}`
        : `<p class="sub">Nothing logged from a session yet. You have ${fmt(trainCount)} training or peak reading${trainCount === 1 ? '' : 's'}. Put one in below after your next session.</p>`}
      <div class="hform" style="margin-top:14px">
        ${field('hTraining', 'trainingHr')}
        ${field('hPeak', 'peakHr')}
      </div>
      <div class="hactions"><button class="btn primary" id="hHeartSave">Save to today</button></div>
      <p class="sub" style="margin-top:12px">Read them off your watch or strap after a session. They go on today's entry with everything else.</p>
    </div>`;

  bind('#hHeartSave', 'click', async () => {
    const body = {};
    for (const [id, key] of [['hTraining', 'trainingHr'], ['hPeak', 'peakHr']]) {
      const raw = (panel.querySelector(`#${id}`)?.value ?? '').trim();
      if (raw !== '') body[key] = Number(raw);
    }
    if (!Object.keys(body).length) return ctx.toast('Put a heart rate in first.', true);
    await post(body, 'Saved to today.');
  });
}

/* ======================================================= 4. TRAINING LOAD */

async function paintLoad() {
  panel.innerHTML = '<div class="card"><h3>Training load</h3><p class="sub">Lining your logs up against the sessions you did.</p></div>';
  const list = await sessions();
  if (S.tab !== 'load') return;

  // Sessions, counted on the day they started, in the player's own days.
  const attempts = {}, count = {};
  for (const s of Array.isArray(list) ? list : []) {
    const d = dayOfIso(s?.startedAt);
    if (!d) continue;
    attempts[d] = (attempts[d] || 0) + n0(s.attempts);
    count[d] = (count[d] || 0) + 1;
  }
  // The empty run before anything happened is trimmed off, counting a session
  // as something that happened, so a day they trained is never cut away.
  const full = dayGrid(LOAD_DAYS);
  const trimmed = trimToData(full, [...SCALES.map((key) => seriesFrom(full, key)), full.map((d) => n0(attempts[d]) || null)]);
  const grid = trimmed.grid;
  const series = SCALES.map((key, i) => ({ key, label: cap(key), color: COLOR[key], values: trimmed.seriesList[i] }));
  const logged = grid.filter((d, i) => series.some((s) => s.values[i] != null)).length;
  const bars = grid.map((d) => n0(attempts[d]));
  const trained = grid.map((d) => Boolean(count[d]));

  panel.innerHTML = `
    <div class="card">
      <h3>How training is landing</h3>
      ${logged >= 3 ? `${chart({
        grid, min: 1, max: 5, ticks: [1, 3, 5], unit: 'out of 5',
        series, bars: { values: bars, color: COLOR.session },
        aria: `Fatigue, soreness and energy against the sessions you trained over the last ${LOAD_DAYS} days`,
      })}${legend([...series.map((s) => ({ label: s.label, color: s.color })), { label: 'Shots in a session', color: COLOR.session, faint: true }])}
      <p class="sub">The bars are the shots you put up that day. The lines are how you said you felt. A line breaks where you logged nothing.</p>`
        : `<p class="sub">Not enough logged yet. You have rated ${fmt(logged)} day${logged === 1 ? '' : 's'} in the last ${LOAD_DAYS}, and the chart starts at three. Rate today on the Today tab.</p>`}
      ${S.sessionsErr ? `<p class="sub">Your sessions did not load, so the bars are missing: ${esc(S.sessionsErr)}</p>` : ''}
    </div>
    <div class="card">
      <h3>The read</h3>
      ${readLoad(grid, series, trained, attempts).map((line) => `<p class="hread">${esc(line)}</p>`).join('')}
    </div>
    <div class="card">
      <h3>Day by day</h3>
      <div class="hdays">
        <div class="hday hhead"><span class="hd-day">Day</span><span class="hd-train">Trained</span><span class="hd-f">Fatigue</span><span class="hd-s">Soreness</span><span class="hd-e">Energy</span></div>
        ${grid.slice(-14).reverse().map((d) => {
          const i = grid.indexOf(d);
          const dot = (v, color) => (v == null ? '<span class="hnil">not logged</span>' : `<span class="hdot" style="--c:${color}">${v}</span>`);
          return `<div class="hday">
            <span class="hd-day"><b>${esc(dayLabel(d))}</b></span>
            <span class="hd-train">${count[d] ? `${fmt(count[d])} session${count[d] === 1 ? '' : 's'}, ${fmt(attempts[d])} shots` : '<span class="hnil">rest</span>'}</span>
            <span class="hd-f">${dot(series[0].values[i], COLOR.fatigue)}</span>
            <span class="hd-s">${dot(series[1].values[i], COLOR.soreness)}</span>
            <span class="hd-e">${dot(series[2].values[i], COLOR.energy)}</span>
          </div>`;
        }).join('')}
      </div>
    </div>`;
}

/**
 * What the last two weeks actually say, in plain sentences built only from
 * what is logged. It names the stack of hard days and stops there: no
 * prescription, nothing medical.
 */
function readLoad(grid, series, trained, attempts) {
  const out = [];
  const [fatigue, soreness, energy] = series.map((s) => s.values);
  const last = (values) => { for (let i = values.length - 1; i >= 0; i--) if (values[i] != null) return { i, v: values[i] }; return null; };

  // Days trained in a row, counting back from the last day they trained.
  let end = -1;
  for (let i = grid.length - 1; i >= 0; i--) if (trained[i]) { end = i; break; }
  let run = 0;
  if (end >= 0) { let i = end; while (i >= 0 && trained[i]) { run++; i--; } }
  const week = trained.slice(-7).filter(Boolean).length;
  const shots = grid.slice(-7).reduce((a, d) => a + n0(attempts[d]), 0);

  if (run >= 3) {
    const rated = [];
    for (let i = end - run + 1; i <= end; i++) if (fatigue[i] != null) rated.push(fatigue[i]);
    out.push(rated.length
      ? `You trained ${run} days in a row. Your fatigue on those days went ${rated.join(', ')} out of 5.`
      : `You trained ${run} days in a row, and did not rate your fatigue on any of them.`);
  } else if (week) {
    out.push(`You trained ${week} day${week === 1 ? '' : 's'} in the last week, ${fmt(shots)} shots in all.`);
  } else {
    out.push('No sessions in the last week. Nothing is stacking up right now.');
  }

  const hard = [];
  for (let i = grid.length - 1; i >= 0; i--) {
    if (fatigue[i] == null) { if (hard.length) break; continue; }
    if (fatigue[i] >= 4) hard.push(grid[i]); else break;
  }
  if (hard.length >= 3) out.push(`That is ${hard.length} days running at 4 or 5 out of 5 on fatigue. Three hard days stacked up is worth an easy one, or a longer night's sleep.`);
  else if (hard.length === 2) out.push('Two days in a row at 4 or 5 on fatigue. Worth watching what tomorrow feels like.');

  const s = last(soreness), e = last(energy), f = last(fatigue);
  const bits = [f && `fatigue ${f.v}`, s && `soreness ${s.v}`, e && `energy ${e.v}`].filter(Boolean);
  if (bits.length) out.push(`Last rated: ${bits.join(', ')}, each out of 5.`);

  const rate = fatigue.filter((v) => v != null).length;
  if (rate < 3) out.push('Rate a few more days and this gets a lot more useful. It takes one tap each.');
  return out;
}

/* =============================================== 5. STRENGTH AND ENDURANCE */

function paintGym() {
  const list = entries();
  const t = todayEntry();
  const kinds = Array.isArray(S.data?.prompts?.enduranceKinds) ? S.data.prompts.enduranceKinds : ['run', 'bike', 'swim', 'court', 'other'];
  const r = ranges();
  const lifts = [];
  for (const e of list) for (const l of e.strength || []) lifts.push({ ...l, at: e.at });
  const blocks = list.filter((e) => e.endurance).map((e) => ({ ...e.endurance, at: e.at }));

  panel.innerHTML = `
    <div class="hgrid">
      <div class="card">
        <h3>Log a lift</h3>
        <div class="hform">
          <label class="hlab wide">Lift<input type="text" id="gLift" maxlength="40" placeholder="back squat" value="${esc(S.lift.lift)}"></label>
          <label class="hlab">Weight <em>${esc(r.kg?.unit || 'kg')}</em><input type="number" id="gKg" inputmode="decimal" min="${r.kg?.min ?? 0}" max="${r.kg?.max ?? 500}" step="${r.kg?.step ?? 0.5}" value="${esc(S.lift.kg)}"></label>
          <label class="hlab">Reps<input type="number" id="gReps" inputmode="numeric" min="${r.reps?.min ?? 1}" max="${r.reps?.max ?? 100}" step="1" value="${esc(S.lift.reps)}"></label>
        </div>
        <div class="hactions"><button class="btn primary" id="gLiftSave">Add to today</button></div>
        ${(t?.strength || []).length ? `<div class="log" style="margin-top:14px">${t.strength.map((l, i) =>
          `<span class="chip">${esc(l.lift)} ${fmt1(l.kg)} kg &times; ${fmt(l.reps)}<button type="button" class="hx" data-lift="${i}" aria-label="Take off ${esc(l.lift)}">&times;</button></span>`).join('')}</div>
          <p class="sub" style="margin-top:10px">Today's lifts. Up to six a day.</p>` : '<p class="sub" style="margin-top:12px">Nothing lifted today yet.</p>'}
      </div>
      <div class="card">
        <h3>Log cardio</h3>
        <div class="hform">
          <label class="hlab">Kind<select id="gKind">${kinds.map((k) =>
            `<option value="${esc(k)}" ${(t?.endurance?.kind || S.cardio.kind) === k ? 'selected' : ''}>${esc(cap(k))}</option>`).join('')}</select></label>
          <label class="hlab">Minutes<input type="number" id="gMin" inputmode="numeric" min="0" max="${r.minutes?.max ?? 600}" step="1" value="${esc(t?.endurance?.minutes ?? S.cardio.minutes)}"></label>
          <label class="hlab">Distance <em>km</em><input type="number" id="gKm" inputmode="decimal" min="0" max="${r.distanceKm?.max ?? 200}" step="0.1" value="${esc(t?.endurance?.distanceKm ?? S.cardio.distanceKm)}"></label>
        </div>
        <div class="hactions"><button class="btn primary" id="gCardioSave">Save today's cardio</button></div>
        <p class="sub" style="margin-top:12px">One cardio block a day. Saving again replaces today's.</p>
      </div>
    </div>
    <div class="card">
      <h3>Personal bests</h3>
      ${bests(lifts, blocks)}
    </div>
    <div class="hgrid">
      <div class="card">
        <h3>Every lift</h3>
        ${lifts.length ? `<div class="hrows">${lifts.slice(0, 30).map((l) =>
          `<div class="hline"><b>${esc(l.lift)}</b><span>${fmt1(l.kg)} kg &times; ${fmt(l.reps)}</span><small>${esc(dayLabel(dayOfIso(l.at)))}</small></div>`).join('')}</div>`
          : '<p class="sub">No lifts logged in the last 90 days.</p>'}
      </div>
      <div class="card">
        <h3>Every cardio block</h3>
        ${blocks.length ? `<div class="hrows">${blocks.slice(0, 30).map((c) =>
          `<div class="hline"><b>${esc(cap(c.kind))}</b><span>${[c.minutes && `${fmt(c.minutes)} min`, c.distanceKm && `${fmt1(c.distanceKm)} km`].filter(Boolean).join(', ')}</span><small>${esc(dayLabel(dayOfIso(c.at)))}</small></div>`).join('')}</div>`
          : '<p class="sub">No cardio logged in the last 90 days.</p>'}
      </div>
    </div>`;

  bind('#gLift', 'input', (e) => { S.lift.lift = e.target.value; });
  bind('#gKg', 'input', (e) => { S.lift.kg = e.target.value; });
  bind('#gReps', 'input', (e) => { S.lift.reps = e.target.value; });
  bind('#gLiftSave', 'click', async () => {
    const lift = (panel.querySelector('#gLift')?.value || '').trim();
    const kg = (panel.querySelector('#gKg')?.value || '').trim();
    const reps = (panel.querySelector('#gReps')?.value || '').trim();
    if (!lift) return ctx.toast('Name the lift first.', true);
    if (kg === '' && reps === '') return ctx.toast('Put the weight or the reps in.', true);
    S.lift = { lift: '', kg: '', reps: '' };
    await post({ strength: [{ lift, kg: kg === '' ? 0 : Number(kg), reps: reps === '' ? 1 : Number(reps) }] }, 'Lift logged.');
  });
  bind('.hx', 'click', async (e) => {
    e.stopPropagation();
    const i = Number(e.currentTarget.dataset.lift);
    const cur = todayEntry();
    if (!cur) return;
    const kept = (cur.strength || []).filter((_, n) => n !== i);
    try {
      await ctx.api(hurl(`/${encodeURIComponent(cur.id)}`), { method: 'PATCH', body: JSON.stringify({ strength: kept }) });
      ctx.toast('Took that one off.');
      await refresh();
    } catch (err) { ctx.toast(err.message, true); }
  });
  bind('#gKind', 'change', (e) => { S.cardio.kind = e.target.value; });
  bind('#gCardioSave', 'click', async () => {
    const kind = panel.querySelector('#gKind')?.value || 'other';
    const minutes = (panel.querySelector('#gMin')?.value || '').trim();
    const distanceKm = (panel.querySelector('#gKm')?.value || '').trim();
    if (minutes === '' && distanceKm === '') return ctx.toast('Put the minutes or the distance in.', true);
    S.cardio = { kind, minutes: '', distanceKm: '' };
    await post({ endurance: { kind, minutes: minutes === '' ? 0 : Number(minutes), distanceKm: distanceKm === '' ? 0 : Number(distanceKm) } }, 'Cardio saved.');
  });
}

/** Heaviest per lift, and the longest of each kind of cardio. Effort, not bodies. */
function bests(lifts, blocks) {
  const byLift = new Map();
  for (const l of lifts) {
    const key = l.lift.toLowerCase();
    const best = byLift.get(key);
    if (!best || n0(l.kg) > n0(best.kg)) byLift.set(key, l);
  }
  const byKind = new Map();
  for (const c of blocks) {
    const best = byKind.get(c.kind);
    if (!best || n0(c.distanceKm) > n0(best.distanceKm) || (n0(c.distanceKm) === n0(best.distanceKm) && n0(c.minutes) > n0(best.minutes))) byKind.set(c.kind, c);
  }
  if (!byLift.size && !byKind.size) return '<p class="sub">Log a lift or a cardio block and your bests show up here.</p>';
  const tiles = [
    ...[...byLift.values()].sort((a, b) => n0(b.kg) - n0(a.kg)).slice(0, 6).map((l) =>
      `<div class="stat"><b>${fmt1(l.kg)}</b><small>${esc(l.lift)}, ${fmt(l.reps)} rep${l.reps === 1 ? '' : 's'}</small></div>`),
    ...[...byKind.values()].map((c) =>
      `<div class="stat"><b>${c.distanceKm ? fmt1(c.distanceKm) : fmt(c.minutes)}</b><small>${esc(cap(c.kind))} best, ${c.distanceKm ? 'km' : 'minutes'}</small></div>`),
  ];
  return `<div class="grid-stats">${tiles.join('')}</div>
    <p class="sub" style="margin-top:12px">Your heaviest logged lift of each kind, and your longest cardio block, over the last ${WINDOW_DAYS} days.</p>`;
}

/* ------------------------------------------------------------- the sheet */

function closeSheet() { root?.querySelector('#hModal')?.classList.remove('open'); }

/**
 * One entry, editable. Every box is the entry as it now reads, so clearing one
 * and saving takes that reading off. The lifts come off with their own button.
 */
function openSheet(entry) {
  if (!entry) return;
  const sheet = root.querySelector('#hSheet');
  if (!sheet) return;
  let strength = (entry.strength || []).map((l) => ({ ...l }));
  const r = ranges();
  const field = (key) => {
    const b = r[key] || {};
    const v = entry[key];
    return `<label class="hlab">${esc(b.label || key)} <em>${esc(b.unit || '')}</em>
      <input type="number" data-f="${esc(key)}" inputmode="decimal" min="${b.min ?? 0}" max="${b.max ?? 999}" step="${b.step ?? 1}" value="${v == null ? '' : esc(String(v))}"></label>`;
  };
  const scale = (key) => `<label class="hlab">${esc(cap(key))} <em>1 to 5</em>
    <select data-f="${esc(key)}"><option value="">Not rated</option>${[1, 2, 3, 4, 5].map((v) =>
      `<option value="${v}" ${Number(entry[key]) === v ? 'selected' : ''}>${v}</option>`).join('')}</select></label>`;
  const moods = Array.isArray(S.data?.prompts?.moods) ? S.data.prompts.moods : [];

  const paintLifts = () => (strength.length
    ? `<div class="log">${strength.map((l, i) => `<span class="chip">${esc(l.lift)} ${fmt1(l.kg)} kg &times; ${fmt(l.reps)}<button type="button" class="hx" data-i="${i}" aria-label="Take off ${esc(l.lift)}">&times;</button></span>`).join('')}</div>`
    : '<p class="sub">No lifts on this one.</p>');

  sheet.innerHTML = `
    <h2>${esc(dayLabel(dayOfIso(entry.at)))}</h2>
    <p class="sub">${esc(new Date(entry.at).toLocaleString())}</p>
    <section>
      <h4>Numbers</h4>
      <div class="hform">
        ${field('weightKg')}${field('heightCm')}${field('sleepHours')}${field('waterCups')}
        ${field('restingHr')}${field('trainingHr')}${field('peakHr')}
      </div>
    </section>
    <section>
      <h4>How it felt</h4>
      <div class="hform">
        ${scale('fatigue')}${scale('soreness')}${scale('energy')}
        <label class="hlab">Mood<select data-f="mood"><option value="">Not said</option>${moods.map((m) =>
          `<option value="${esc(m)}" ${entry.mood === m ? 'selected' : ''}>${esc(cap(m))}</option>`).join('')}</select></label>
        <label class="hlab wide">Note<input type="text" data-f="note" maxlength="500" value="${esc(entry.note || '')}"></label>
      </div>
    </section>
    <section>
      <h4>Lifts</h4>
      <div id="hSheetLifts">${paintLifts()}</div>
    </section>
    ${entry.endurance ? `<section><h4>Cardio</h4><p>${esc(cap(entry.endurance.kind))}${
      entry.endurance.minutes ? `, ${fmt(entry.endurance.minutes)} minutes` : ''}${
      entry.endurance.distanceKm ? `, ${fmt1(entry.endurance.distanceKm)} km` : ''}</p></section>` : ''}
    <div class="hactions">
      <button class="btn primary" id="hSheetSave">Save changes</button>
      <button class="btn" id="hSheetClose">Close</button>
      <button class="btn" id="hSheetDelete" style="color:var(--bad)">Delete</button>
    </div>
    <p class="hplain">${esc(S.data?.prompts?.notMedical || NOT_MEDICAL)}</p>`;
  root.querySelector('#hModal').classList.add('open');

  const bindLifts = () => sheet.querySelectorAll('#hSheetLifts .hx').forEach((b) => b.addEventListener('click', () => {
    strength = strength.filter((_, i) => i !== Number(b.dataset.i));
    const box = sheet.querySelector('#hSheetLifts');
    if (box) { box.innerHTML = paintLifts(); bindLifts(); }
  }));
  bindLifts();

  sheet.querySelector('#hSheetClose').addEventListener('click', closeSheet);
  sheet.querySelector('#hSheetSave').addEventListener('click', async () => {
    const body = { strength };
    sheet.querySelectorAll('[data-f]').forEach((el) => {
      const key = el.dataset.f;
      const raw = String(el.value ?? '').trim();
      // An empty box means the reading comes off the entry. The Worker reads
      // null as "clear this one".
      body[key] = raw === '' ? null : (el.tagName === 'SELECT' && key === 'mood' ? raw : (el.type === 'number' ? Number(raw) : raw));
    });
    if (body.mood === '') body.mood = null;
    try {
      await ctx.api(hurl(`/${encodeURIComponent(entry.id)}`), { method: 'PATCH', body: JSON.stringify(body) });
      closeSheet();
      ctx.toast('Entry updated.');
      await refresh();
      ctx.refresh?.journal?.();
    } catch (e) { ctx.toast(e.message, true); }
  });
  sheet.querySelector('#hSheetDelete').addEventListener('click', async () => {
    try {
      const ok = await ctx.ui.confirm({
        title: 'Delete this entry?',
        body: 'Everything you logged on this day comes off your charts.',
        confirmText: 'Delete', danger: true,
      });
      if (!ok) return;
      await ctx.api(hurl(`/${encodeURIComponent(entry.id)}`), { method: 'DELETE' });
      closeSheet();
      ctx.toast('Entry deleted.');
      await refresh();
      ctx.refresh?.journal?.();
    } catch (e) { ctx.toast(e.message, true); }
  });
}

/* --------------------------------------------------------------- charting
 * Inline SVG, no library, both themes. Colours go in style attributes, which
 * is where var() works. Every chart sits on a day grid, so a week nobody
 * logged reads as a gap instead of a straight line through it. */

const CW = 640, CH = 220, CP = { l: 46, r: 14, t: 16, b: 34 };

/** Values per day from trend points, last write on a day wins. */
function seriesOn(grid, points) {
  const map = new Map();
  for (const p of points || []) { const d = dayOfIso(p.at); if (d) map.set(d, Number(p.value)); }
  return grid.map((d) => (map.has(d) ? map.get(d) : null));
}
/** Values per day straight off the entries, for a field with no trend. */
function seriesFrom(grid, key) {
  const map = new Map();
  for (const e of entries()) {
    const v = Number(e[key]);
    const d = dayOfIso(e.at);
    if (d && Number.isFinite(v)) map.set(d, v);
  }
  return grid.map((d) => (map.has(d) ? map.get(d) : null));
}

/**
 * Drop the empty run at the front of a window. A player three weeks in should
 * see three weeks, not their line squeezed into the last fifth of ninety days.
 * The axis still carries real dates, so nothing is being hidden.
 */
function trimToData(grid, seriesList) {
  let start = grid.length;
  for (const values of seriesList) {
    const i = values.findIndex((v) => v != null);
    if (i >= 0 && i < start) start = i;
  }
  if (start >= grid.length || start <= 2) return { grid, seriesList };
  const from = start - 2;                      // a little room before the first dot
  return { grid: grid.slice(from), seriesList: seriesList.map((v) => v.slice(from)) };
}

/** Sane top and bottom for a chart that is not about zero, like a body weight. */
function bounds(values) {
  const nums = values.filter((v) => v != null && Number.isFinite(v));
  if (!nums.length) return { min: 0, max: 1 };
  let lo = Math.min(...nums), hi = Math.max(...nums);
  if (lo === hi) { lo -= 1; hi += 1; }
  const pad = (hi - lo) * 0.12;
  return { min: round1(Math.max(0, lo - pad)), max: round1(hi + pad) };
}

/**
 * Runs of consecutive readings. A gap of more than two empty days ends the run
 * so the line never pretends to know what happened in between.
 */
function runs(values, maxGap = 3) {
  const out = [];
  let cur = null, last = -99;
  values.forEach((v, i) => {
    if (v == null) return;
    if (!cur || i - last > maxGap) { cur = []; out.push(cur); }
    cur.push([i, v]);
    last = i;
  });
  return out.filter((r) => r.length >= 2);
}

function chart({ grid, series = [], bars = null, min = 0, max = 1, ticks = null, unit = '', aria = '' }) {
  const inner = CW - CP.l - CP.r;
  const x = (i) => (grid.length < 2 ? CP.l + inner / 2 : CP.l + (i / (grid.length - 1)) * inner);
  const y = (v) => CP.t + (1 - (v - min) / ((max - min) || 1)) * (CH - CP.t - CP.b);
  const tickVals = ticks || [min, round1((min + max) / 2), max];
  const rules = tickVals.map((v) => `
    <line x1="${CP.l}" y1="${y(v).toFixed(1)}" x2="${CW - CP.r}" y2="${y(v).toFixed(1)}" style="stroke:var(--line)" stroke-width="1"></line>
    <text x="${CP.l - 8}" y="${(y(v) + 4).toFixed(1)}" text-anchor="end" style="fill:var(--mute);font:700 11px var(--mono)">${esc(fmt1(v))}</text>`).join('');
  const marks = [...new Set([0, Math.floor((grid.length - 1) / 2), grid.length - 1])].filter((i) => i >= 0 && i < grid.length);
  // The end labels anchor inward. Centred on the last point, a date runs off
  // the right of the viewBox and loses its last characters.
  const labels = marks.map((i) => {
    const anchor = i === 0 && grid.length > 1 ? 'start' : (i === grid.length - 1 && grid.length > 1 ? 'end' : 'middle');
    return `<text x="${x(i).toFixed(1)}" y="${CH - 11}" text-anchor="${anchor}" style="fill:var(--mute);font:700 11px var(--mono)">${esc(shortDay(grid[i]))}</text>`;
  }).join('');
  const unitLabel = `<text x="4" y="11" style="fill:var(--dim);font:700 10px var(--mono)">${esc(unit)}</text>`;

  let barSvg = '';
  if (bars && bars.values.some((v) => n0(v) > 0)) {
    const top = Math.max(...bars.values.map(n0));
    const w = Math.max(3, Math.min(13, (inner / Math.max(1, grid.length)) * 0.6));
    barSvg = bars.values.map((v, i) => {
      if (!n0(v)) return '';
      const h = Math.max(3, (n0(v) / top) * (CH - CP.t - CP.b) * 0.4);
      return `<rect x="${(x(i) - w / 2).toFixed(1)}" y="${(CH - CP.b - h).toFixed(1)}" width="${w.toFixed(1)}" height="${h.toFixed(1)}" rx="2" style="fill:${bars.color};opacity:.3"></rect>`;
    }).join('');
  }

  const lines = series.map((s) => {
    const paths = runs(s.values).map((run) =>
      `<polyline points="${run.map(([i, v]) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')}" fill="none" style="stroke:${s.color}" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"></polyline>`).join('');
    const dots = s.values.map((v, i) => (v == null ? '' : `<circle cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="3" style="fill:${s.color}"></circle>`)).join('');
    return paths + dots;
  }).join('');

  return `<svg class="hchart" viewBox="0 0 ${CW} ${CH}" role="img" aria-label="${esc(aria)}">${rules}${labels}${unitLabel}${barSvg}${lines}</svg>`;
}

const legend = (items) => `<div class="hlegend">${items.map((s) =>
  `<span><i style="background:${s.color}${s.faint ? ';opacity:.45' : ''}"></i>${esc(s.label)}</span>`).join('')}</div>`;
